import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MagicLink from "../../MagicLink";
import LinkContainer from "./LinkContainer";
import styles from "./index.module.css";
import editIcon from "../../../../assets/icons/mobileIcons/edit.png";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import {
    setLinksChanged,
    setNonStoreSectionsChanged,
    setSelectedLinkInBio,
    setSelectedScreen,
    setStoreSectionsChanged
} from "../../../../redux/slice/dashboardSlice";
import { arrayMoveImmutable } from "array-move";
import { findDiff } from "../../../Builder/builderServices/utils";
import DeleteLinkPopup from "../../DeleteSectionPopup";
import { vibrateDevice } from "../../utils";

const SortableList = SortableContainer(({ items }) => {
    const [isMenuOpened, setIsMenuOpened] = useState(-1);
    const brandsIcons = useSelector((store) => store.root.dashboard.brandsIcons);

    useEffect(() => {
        const handleClickOutside = () => {
            setIsMenuOpened(-1);
        };
        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.collectionsContainer}>
            {items.map((link, index) => (
                <>
                    {!link.isDeleted && (
                        <SortableItem
                            key={`item-${index}`}
                            index={index}
                            value={link}
                            brandsIcons={brandsIcons}
                            showDivider={items.length - 1 !== index}
                            isMenuOpened={isMenuOpened === index}
                            setIsMenuOpened={() => {
                                setIsMenuOpened(index);
                            }}
                            closeMenu={() => {
                                setIsMenuOpened(-1);
                            }}
                        />
                    )}
                </>
            ))}
        </div>
    );
});

const SortableItem = SortableElement(({ value, showDivider, isMenuOpened, setIsMenuOpened, brandsIcons, closeMenu }) => (
    <LinkContainer
        link={value}
        showDivider={showDivider}
        isMenuOpened={isMenuOpened}
        setIsMenuOpened={setIsMenuOpened}
        closeMenu={closeMenu}
        brandsIcons={brandsIcons}
    />
));

const index = () => {
    const dispatch = useDispatch();
    const {
        selectedHeader,
        selectedCollection,
        selectedLinkInBio,
        storeSectionsChanged,
        nonStoreSectionsChanged,
        isSectionPickModalOpen,
        magicLinkResponse,
        deletingLink,
        linksDiff,
        linksChanged,
        isSectionDeleteModalOpen,
        isSectionCreateModalOpen
    } = useSelector((state) => state.root.dashboard);

    const [list, setList] = useState(selectedLinkInBio.links);
    const [sectionHeading, setSectionHeading] = useState(selectedLinkInBio.title);
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);
    const [isAddSectionOpened, setIsAddSectionOpened] = useState(false);

    const onSortStart = () => {
        vibrateDevice(80);
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (newIndex === oldIndex) {
            return;
        }
        setList(arrayMoveImmutable(list, oldIndex, newIndex));
        let sectionCopy = { ...selectedLinkInBio };
        let tempSortId = sectionCopy.links[newIndex].sortId;
        if (newIndex > oldIndex) {
            sectionCopy.links = sectionCopy.links.map((link, i) => {
                if (i > oldIndex && i <= newIndex) {
                    return {
                        ...link,
                        sortId: sectionCopy.links[i - 1].sortId
                    };
                }
                return link;
            });
        } else if (oldIndex > newIndex) {
            sectionCopy.links = sectionCopy.links.map((link, i) => {
                if (i >= newIndex && i < oldIndex) {
                    return {
                        ...link,
                        sortId: sectionCopy.links[i + 1].sortId
                    };
                }
                return link;
            });
        }
        sectionCopy.links[oldIndex] = {
            ...sectionCopy.links[oldIndex],
            sortId: tempSortId
        };
        const item = sectionCopy.links.splice(oldIndex, 1)[0];
        sectionCopy.links.splice(newIndex, 0, item);
        let updatedSections;
        if (selectedHeader === "LinkInBio") {
            updatedSections = nonStoreSectionsChanged.map((section) => (section.id === sectionCopy.id ? sectionCopy : section));
            dispatch(setSelectedLinkInBio(sectionCopy));
            dispatch(setNonStoreSectionsChanged(updatedSections));
        }
        dispatch(setLinksChanged());
        findDiff();
    };

    const handleChange = (e) => {
        setSectionHeading(e.target.value);
        let updatedSections;
        let section = { ...selectedLinkInBio, title: e.target.value };

        if (selectedHeader === "LinkInBio") {
            updatedSections = nonStoreSectionsChanged.map((e) => {
                return e.id === section.id ? section : e;
            });
            dispatch(setSelectedLinkInBio(section));
            dispatch(setNonStoreSectionsChanged(updatedSections));
        }
        dispatch(setLinksChanged());
        findDiff();
    };
    useEffect(() => {
        history.pushState(null, null, window.location.href);
        const handleBackButton = () => {
            dispatch(setSelectedLinkInBio(null));
            dispatch(setSelectedScreen(null));
        };
        window.addEventListener("popstate", handleBackButton);

        return () => {
            window.removeEventListener("popstate", handleBackButton);
            dispatch(setSelectedLinkInBio(null));
            dispatch(setSelectedScreen(null));
        };
    }, []);

    useEffect(() => {
        const updatedSection = nonStoreSectionsChanged.filter((section) => section.id === selectedLinkInBio.id)[0];
        setList(updatedSection.links);
    }, [nonStoreSectionsChanged]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus(); // Focus the input when it is rendered
        }
    }, [isEditing]); // Depend on isEditing

    return (
        <div className={styles.container}>
            <MagicLink screen="LinkInBio" />
            <div className={styles.titleContainer}>
                {!isEditing ? (
                    <>
                        <span className={styles.titleSpan} size={10}>
                            {selectedLinkInBio.title}
                        </span>
                        <img
                            src={editIcon}
                            className={styles.editImg}
                            onClick={() => {
                                setIsEditing(true);
                            }}
                        />
                    </>
                ) : (
                    <input
                        ref={inputRef}
                        className={styles.titleInput}
                        value={sectionHeading}
                        onChange={handleChange}
                        onBlur={() => {
                            setIsEditing(false);
                        }}
                    />
                )}
            </div>
            <SortableList
                onSortStart={onSortStart}
                onSortEnd={onSortEnd}
                items={list.filter((item) => item.isDeleted === false)}
                pressDelay={500}
                lockAxis="y"
            />
            <div style={{ height: "100px" }}></div>
        </div>
    );
};

export default index;
